/**
 * *Leaving with unsaved measurements* — `docs/property-maps-v2.md` §5.4,
 * board 6 tile 4.
 *
 * A measure session lives in the browser (`measure-session.ts`), so walking
 * away from it is the one way to lose a morning's work. Three ways out and no
 * default: **Discard** throws the tape away, **Keep drawing** goes back, and
 * **Save to property** opens the review dialog — which is still the only
 * thing that writes.
 *
 * It is deliberately not a `ConfirmDialog`: that component has one action and
 * a cancel, and the whole point here is that *Save* and *Discard* are both
 * real answers rather than one being the safe one.
 */
import { Button } from '../../components/primitives.js'
import { cn } from '../../lib/cn.js'
import { SheetOrDialog } from '../../components/sheet-or-dialog.js'
import { IconWarn } from '../../frame/icons.js'

export function LeavingDialog({ count, onDiscard, onStay, onSave }: {
  count: number
  onDiscard: () => void
  onStay: () => void
  onSave: () => void
}) {
  return (
    <SheetOrDialog
      onClose={onStay}
      title={`${count} measurement${count === 1 ? '' : 's'} not saved`}
      width={440}
      mark={<IconWarn size={16} />}
      /*
       * §6.4: stacked full-width on a phone, destructive last — three
       * buttons side by side in a 390px footer is three buttons nobody can
       * read, and the one that throws work away must not be the one a thumb
       * reaches first. `flex-row-reverse` is what puts Discard at the bottom
       * of the stack while keeping it leftmost in the desktop row.
       */
      footer={(
        <div className={cn(
          'flex w-full flex-col-reverse gap-2',
          'sm:flex-row sm:[&>button]:w-auto [&>button]:w-full',
        )}
        >
          <Button onClick={onDiscard}>Discard</Button>
          <Button onClick={onStay}>Keep drawing</Button>
          <Button variant="primary" onClick={onSave}>Save to property</Button>
        </div>
      )}
    >
      <p className="m-0 text-body leading-relaxed text-text-body">
        {count === 1
          ? 'One outline has not been added to the property yet.'
          : `${count} outlines have not been added to the property yet.`}
        {' They are not saved anywhere until you add them, so leaving now loses them.'}
      </p>
    </SheetOrDialog>
  )
}
